'use client';

import { useState } from 'react';

import { PATHS } from 'routes/routes.constants';

import useQueryParams from '@polyai/common/hooks/useQueryParams';
import { useRouter } from '@polyai/common/hooks/useRouter';
import Button from '@polyai/ui/components/atoms/Button';
import { Stack } from '@polyai/ui/components/atoms/Stack';

import CalendlyModal from 'components/organisms/CalendlyModal';
import OTError from 'components/organisms/OTError/OTError';
import useAccount from 'hooks/useAccount';
import useExtendTrial from 'hooks/useExtendTrial';
import { usePendo } from 'hooks/usePendo';
import { generateNextPath } from 'lib/routeHelper';
import { CALENDLY_URL } from '../../constants/Calendly.constants';
import { TRIAL_EXPIRED } from './TrialExpired.uiCopy';

const TRIAL_CTA_LINK = 'https://poly.ai/request-a-demo/';

export default function TrialExpired() {
  const [open, setOpen] = useState(false);
  const [callScheduled, setCallScheduled] = useState(false);
  const searchParams = useQueryParams();
  const { pushUrl } = useRouter();
  const { accountId } = useAccount();

  usePendo({ accountId });

  const isPlg = searchParams.get('plg') === 'true';
  const isDisabled = searchParams.get('disabled') === 'true';

  const {
    extendTrial,
    isLoading: isLoadingExtendTrial,
    canExtend,
  } = useExtendTrial();

  const handleExtendTrial = async () => {
    const success = await extendTrial();
    if (success) {
      pushUrl(generateNextPath(PATHS.ASSISTANTS, { accountId }));
    }
  };

  // Account disabled - simple message
  if (isDisabled) {
    return (
      <OTError
        description={TRIAL_EXPIRED.disabled.description}
        title={TRIAL_EXPIRED.disabled.title}
      />
    );
  }

  // PLG trial expired
  if (isPlg) {
    return (
      <OTError
        description={
          canExtend
            ? TRIAL_EXPIRED.plg.descriptionCanExtend
            : TRIAL_EXPIRED.plg.descriptionCannotExtend
        }
        title={TRIAL_EXPIRED.plg.title}
      >
        <Stack direction="row" spacing="sm1" marginTop="md2">
          {canExtend && (
            <Button
              label={TRIAL_EXPIRED.plg.extendButton}
              data-pendo-id="plg-lockout-page-extend-trial"
              disabled={isLoadingExtendTrial}
              variant="SECONDARY"
              inverse
              loading={isLoadingExtendTrial}
              onClick={handleExtendTrial}
            />
          )}
          <Button
            label={TRIAL_EXPIRED.plg.contactButton}
            data-pendo-id="plg-lockout-page-contact-button"
            variant="PRIMARY"
            inverse
            onClick={() =>
              window.open(TRIAL_CTA_LINK, '_blank', 'noopener,noreferrer')
            }
          />
        </Stack>
      </OTError>
    );
  }

  // OpenTable trial expired - with contact button and Calendly
  return (
    <>
      <OTError
        description={callScheduled ? null : TRIAL_EXPIRED.openTable.description}
        pendoId="vertical-lockout-page-contact-button"
        retryLabel={
          callScheduled
            ? TRIAL_EXPIRED.openTable.rescheduleButton
            : TRIAL_EXPIRED.openTable.contactButton
        }
        title={
          callScheduled
            ? TRIAL_EXPIRED.openTable.titleScheduled
            : TRIAL_EXPIRED.openTable.title
        }
        onRetry={() => {
          setOpen(true);
        }}
      />

      <CalendlyModal
        open={open}
        url={CALENDLY_URL}
        onEventScheduled={() => {
          setCallScheduled(true);
        }}
        onOpenChange={setOpen}
      />
    </>
  );
}
